/**
 * App header — logo, org switcher, user menu, theme toggle.
 */
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { useAuth } from '../Auth/AuthContext'
import ThemeToggle from './ThemeToggle'

export default function Header() {
  const { user, orgs, currentOrg, switchOrg, signOut } = useAuth()
  const location = useLocation()
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [showOrgDropdown, setShowOrgDropdown] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/import', label: 'Import' },
    { to: '/fund-variables', label: 'Fund Variables' },
    { to: '/document-coverage', label: 'Document coverage' },
    { to: '/cash-requirements', label: 'Cash Requirements' },
    { to: '/admin/classifications', label: 'Admin' },
  ]

  return (
    <header className="bg-surface-secondary border-b border-edge-subtle">
      <div className="app-container flex items-center justify-between py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/dashboard" className="truncate text-base sm:text-lg font-bold text-ink hover:text-brand transition">
            PE Dashboard
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                location.pathname === to
                  ? 'bg-surface-tertiary text-ink'
                  : 'text-ink-muted hover:text-ink hover:bg-surface-tertiary/50'
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>
        </div>

        {/* Org Switcher */}
        {orgs.length > 0 && (
          <div className="relative hidden lg:block">
            <button
              onClick={() => setShowOrgDropdown(!showOrgDropdown)}
              className="flex max-w-[280px] items-center gap-2 px-3 py-1.5 bg-surface-tertiary rounded-lg text-sm text-ink-muted hover:text-ink transition"
            >
              <span className="truncate">{currentOrg?.name || 'Select Organization'}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showOrgDropdown && (
              <div className="absolute top-full left-0 mt-1 bg-surface-tertiary border border-edge rounded-lg shadow-xl z-50 min-w-[200px]">
                {orgs.map(org => (
                  <button
                    key={org.id}
                    onClick={() => { switchOrg(org.id); setShowOrgDropdown(false) }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-surface-secondary transition first:rounded-t-lg last:rounded-b-lg ${
                      currentOrg?.id === org.id ? 'text-brand' : 'text-ink-muted'
                    }`}
                  >
                    <div>{org.name}</div>
                    <div className="text-xs text-ink-subtle mt-0.5">{org.role}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

      <div className="flex items-center gap-2 sm:gap-3">
        <ThemeToggle />

        {/* User Menu */}
        <div className="relative hidden lg:block">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition"
          >
            <div className="w-8 h-8 bg-brand rounded-full flex items-center justify-center text-ink-onAccent font-medium text-xs">
              {user?.email?.[0]?.toUpperCase() || '?'}
            </div>
            <span className="hidden sm:inline">{user?.email}</span>
          </button>

          {showUserMenu && (
            <div className="absolute top-full right-0 mt-1 bg-surface-tertiary border border-edge rounded-lg shadow-xl z-50 min-w-[180px]">
              <div className="px-4 py-3 border-b border-edge">
                <div className="text-sm text-ink">{user?.email}</div>
                <div className="text-xs text-ink-subtle mt-0.5">{currentOrg?.role || 'member'}</div>
              </div>
              <button
                onClick={() => { signOut(); setShowUserMenu(false) }}
                className="w-full text-left px-4 py-2.5 text-sm text-danger-fg hover:bg-surface-secondary rounded-b-lg transition"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
          onClick={() => setShowMobileMenu((v) => !v)}
          className="lg:hidden p-2 rounded-lg border border-edge bg-surface-secondary text-ink-muted hover:text-ink hover:bg-surface-tertiary transition-colors"
        >
          {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      </div>

      {showMobileMenu && (
        <div className="lg:hidden border-t border-edge-subtle">
          <div className="app-container py-3 stack-sm">
            <nav className="grid grid-cols-1 gap-1">
              {navLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setShowMobileMenu(false)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    location.pathname === to
                      ? 'bg-surface-tertiary text-ink'
                      : 'text-ink-muted hover:text-ink hover:bg-surface-tertiary/50'
                  }`}
                >
                  {label}
                </Link>
              ))}
            </nav>

            {orgs.length > 0 && (
              <div className="rounded-lg border border-edge bg-surface-tertiary/60 p-3">
                <div className="text-xs text-ink-subtle mb-2">Organization</div>
                <div className="text-sm text-ink mb-2 truncate">{currentOrg?.name || 'Select Organization'}</div>
                <div className="grid gap-1 max-h-44 overflow-y-auto">
                  {orgs.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => { switchOrg(org.id); setShowMobileMenu(false) }}
                      className={`w-full text-left px-2.5 py-1.5 rounded text-sm transition ${
                        currentOrg?.id === org.id ? 'bg-surface-secondary text-brand' : 'text-ink-muted hover:bg-surface-secondary'
                      }`}
                    >
                      <div className="truncate">{org.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-lg border border-edge bg-surface-tertiary/60 p-3">
              <div className="text-xs text-ink-subtle">Signed in as</div>
              <div className="text-sm text-ink truncate mt-1">{user?.email}</div>
              <button
                onClick={() => { signOut(); setShowMobileMenu(false) }}
                className="mt-3 w-full text-left px-3 py-2 rounded-lg text-sm text-danger-fg hover:bg-surface-secondary transition"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
